import { useCallback, useState } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import axios from 'axios';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Divider,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { API_ENDPOINTS } from '../config/api';

interface RFPData {
  rfpId: string;
  filename: string;
  parsedData: {
    requirements: string[];
    deadline?: string;
    focusAreas: string[];
  };
}

interface RFPUploadProps {
  onRFPUploaded: (data: RFPData) => void;
  proposalNarrative: string;
  setProposalNarrative: (value: string) => void;
  rfpData: RFPData | null;
  onGenerateProposal: () => void;
  onReset: () => void;
  isGenerating: boolean;
}

export default function RFPUpload({
  onRFPUploaded,
  proposalNarrative,
  setProposalNarrative,
  rfpData,
  onGenerateProposal,
  onReset,
  isGenerating
}: RFPUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalidFileDialogOpen, setInvalidFileDialogOpen] = useState(false);
  const [rejectedFileName, setRejectedFileName] = useState<string>('');

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    console.log('=== RFP UPLOAD STARTED ===', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      timestamp: new Date().toISOString()
    });

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('rfp', file);

      console.log('Sending upload request to backend...');
      const uploadStartTime = Date.now();

      const response = await axios.post(API_ENDPOINTS.rfp.upload, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const uploadTime = Date.now() - uploadStartTime;
      console.log('=== RFP UPLOAD SUCCESS ===', {
        uploadTimeMs: uploadTime,
        rfpId: response.data.rfpId,
        filename: response.data.filename,
        requirementsCount: response.data.parsedData?.requirements?.length
      });

      onRFPUploaded(response.data);
    } catch (err) {
      console.error('=== RFP UPLOAD FAILED ===', {
        error: err,
        errorMessage: err instanceof Error ? err.message : 'Unknown error',
        axiosError: axios.isAxiosError(err),
        response: axios.isAxiosError(err) ? err.response?.data : null,
        status: axios.isAxiosError(err) ? err.response?.status : null
      });

      // Handle specific error types from the backend
      if (axios.isAxiosError(err) && err.response) {
        const errorData = err.response.data;
        console.log('Backend error response:', errorData);

        // API quota exceeded
        if (err.response.status === 429) {
          const retryAfter = errorData.retryAfter
            ? ` Please try again in ${Math.ceil(errorData.retryAfter / 1000)} seconds.`
            : '';
          setError(`API quota exceeded.${retryAfter} The service has reached its usage limit.`);
          return;
        }

        // Network or timeout errors
        if (err.response.status === 503 || err.response.status === 504) {
          setError('Network error occurred. Please check your connection and try again.');
          return;
        }

        // API configuration errors
        if (err.response.status === 401 && errorData.error === 'API configuration error') {
          setError('Service is temporarily unavailable. Please contact support.');
          return;
        }

        // Validation or parsing errors
        if (err.response.status === 400) {
          setError(errorData.message || 'Invalid file. Please ensure your document is in a supported format.');
          return;
        }

        // Use backend error message if available
        if (errorData.message) {
          setError(errorData.message);
          return;
        }
      }

      // Network error (no response from server)
      if (axios.isAxiosError(err) && err.code === 'ERR_NETWORK') {
        setError('Unable to connect to the server. Please check your internet connection.');
        return;
      }

      // Generic fallback error
      setError('Failed to upload RFP. Please try again.');
    } finally {
      setUploading(false);
    }
  }, [onRFPUploaded]);

  const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
    if (fileRejections.length > 0) {
      const rejection = fileRejections[0];
      setRejectedFileName(rejection.file.name);
      setInvalidFileDialogOpen(true);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: {
      'text/plain': ['.txt'],
      'text/markdown': ['.md'],
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc']
    },
    maxFiles: 1
  });

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: { xs: 'auto', md: '60vh' },
        py: { xs: 2, md: 4 },
      }}
    >
      <Card
        elevation={3}
        sx={{
          maxWidth: 600,
          width: '100%',
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box textAlign="center" mb={3}>
            <DescriptionIcon
              sx={{
                fontSize: 64,
                color: 'primary.main',
                mb: 2,
              }}
            />
            <Typography variant="h5" component="h2" gutterBottom fontWeight={600}>
              Upload RFP Document
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Get started by uploading your Request for Proposal document
            </Typography>
          </Box>

          {/* Only show drag-and-drop area if no RFP is uploaded */}
          {!rfpData && (
            <Paper
              {...getRootProps()}
              elevation={0}
              sx={{
                border: 3,
                borderStyle: 'dashed',
                borderColor: isDragActive ? 'primary.main' : uploading ? 'action.disabled' : 'divider',
                borderRadius: 3,
                p: { xs: 4, sm: 6 },
                textAlign: 'center',
                cursor: uploading ? 'wait' : 'pointer',
                transition: 'all 0.3s ease',
                backgroundColor: isDragActive
                  ? 'action.hover'
                  : uploading
                  ? 'action.disabledBackground'
                  : 'background.paper',
                '&:hover': {
                  borderColor: uploading ? 'action.disabled' : 'primary.main',
                  backgroundColor: uploading ? 'action.disabledBackground' : 'action.hover',
                },
              }}
            >
              <input {...getInputProps()} />

              {uploading ? (
                <Box>
                  <CircularProgress size={48} sx={{ mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Uploading and analyzing RFP...
                  </Typography>
                </Box>
              ) : isDragActive ? (
                <Box>
                  <CloudUploadIcon
                    sx={{
                      fontSize: 64,
                      color: 'primary.main',
                      mb: 2,
                    }}
                  />
                  <Typography variant="h6" color="primary" gutterBottom>
                    Drop the file here
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <CloudUploadIcon
                    sx={{
                      fontSize: 64,
                      color: 'text.secondary',
                      mb: 2,
                    }}
                  />
                  <Typography variant="h6" gutterBottom>
                    Drag and drop your RFP file here
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    or click to browse
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      mt: 2,
                      px: 2,
                    }}
                  >
                    Supported formats: .txt, .md, .pdf, .docx, .doc
                  </Typography>
                </Box>
              )}
            </Paper>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {/* Show uploaded RFP details if available */}
          {rfpData && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" mb={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircleIcon color="success" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    RFP Uploaded Successfully
                  </Typography>
                </Stack>
                <Button
                  variant="text"
                  color="error"
                  size="small"
                  onClick={onReset}
                  disabled={isGenerating}
                  sx={{ minWidth: 'auto' }}
                >
                  Remove
                </Button>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <DescriptionIcon color="primary" sx={{ fontSize: 20 }} />
                <Typography variant="body2" color="text.secondary">
                  {rfpData.filename}
                </Typography>
              </Stack>
              {rfpData.parsedData?.deadline && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Deadline
                  </Typography>
                  <Typography variant="body2">
                    {rfpData.parsedData.deadline}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Proposal Strategy Field */}
          <Box sx={{ mt: rfpData ? 2 : 4 }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Proposal Strategy (Optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Describe your win strategy, key themes, or positioning for this proposal.
              This will guide the AI in generating targeted content.
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={6}
              maxRows={15}
              value={proposalNarrative}
              onChange={(e) => setProposalNarrative(e.target.value)}
              placeholder="Example: Focus on our 10+ years of experience in healthcare IT, emphasize HIPAA compliance expertise, and highlight our local presence in the client's region..."
              helperText={`${proposalNarrative.length}/1000 characters`}
              inputProps={{ maxLength: 1000 }}
              disabled={!rfpData || isGenerating}
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: '0.95rem',
                  lineHeight: 1.6,
                }
              }}
            />
          </Box>

          {/* Action Button - Only show when RFP is uploaded */}
          {rfpData && (
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={onGenerateProposal}
                disabled={isGenerating}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #5a3a7a 100%)',
                  },
                }}
              >
                {isGenerating ? 'Generating...' : 'Generate Proposal'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Invalid File Format Dialog */}
      <Dialog
        open={invalidFileDialogOpen}
        onClose={() => setInvalidFileDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorOutlineIcon color="error" />
          Invalid File Format
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            The file <strong>{rejectedFileName}</strong> is not a supported format.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Please upload a file in one of the following formats:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 0 }}>
            <li><Typography variant="body2">.txt (Plain Text)</Typography></li>
            <li><Typography variant="body2">.md (Markdown)</Typography></li>
            <li><Typography variant="body2">.pdf (PDF Document)</Typography></li>
            <li><Typography variant="body2">.docx (Microsoft Word 2007+)</Typography></li>
            <li><Typography variant="body2">.doc (Microsoft Word 97-2003)</Typography></li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setInvalidFileDialogOpen(false)}
            variant="contained"
            color="primary"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
